import { useState, useEffect } from 'react';
import { PresskitButton, LinkedButton, ShowCaseGameText, ShowCaseGameTitle } from './RentalSteamButton';

export function ShowCaseGame(props)
{
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState(0);

    // Minimum swipe distance (in px) to trigger navigation
    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setIsAutoPlaying(false);
        setIsDragging(true);
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
        setDragOffset(0);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
        if (isDragging) {
            const currentTouch = e.targetTouches[0].clientX;
            const diff = touchStart - currentTouch;
            // Calculate percentage moved based on screen width
            const percentageMoved = (diff / window.innerWidth) * 100;
            setDragOffset(percentageMoved);
        }
    };

    const onTouchEnd = () => {
        setIsDragging(false);
        setDragOffset(0);
        
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            handleManualNavigation(currentImageIndex === props.displayImages.length - 1 ? 0 : currentImageIndex + 1);
        }
        if (isRightSwipe) {
            handleManualNavigation(currentImageIndex === 0 ? props.displayImages.length - 1 : currentImageIndex - 1);
        }

        // Reset touch positions
        setTouchStart(null);
        setTouchEnd(null);
    };

    useEffect(() => {
        let intervalId;
        if (isAutoPlaying) {
            intervalId = setInterval(() => {
                setCurrentImageIndex((prev) => 
                    prev === props.displayImages.length - 1 ? 0 : prev + 1
                );
            }, 3000);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [isAutoPlaying, props.displayImages.length]);

    const handleManualNavigation = (newIndex) => {
        setIsAutoPlaying(false);
        setCurrentImageIndex(newIndex);
        setTimeout(() => setIsAutoPlaying(true), 5000);
    };

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col xl:flex-row 
        col-span-6 justify-center xl:justify-center lg:items-center xl:items-start
        px-12 mg:px-36 xl:px-36 py-20 pt-36 md:pt-40 xl:pt-32">

        <div className="rental">
          <div className="information font-bold text-center xl:text-left">
            <ShowCaseGameTitle title1Color={props.title1Color} title1={props.title1} title2={props.title2} />
            <div className='hidden xl:flex xl:flex-col'>
              <ShowCaseGameText description={props.description}/>
              <LinkedButton text={props.linkedButtonText} link={props.steamLink} />
              {props.presskitLink && <PresskitButton link={props.presskitLink} />}
            </div>
          </div>
        </div>

        <div className="image flex text-center pt-4 md:pt-8 xl:pt-0
          flex-col xl:flex-row 2xl:flex-row lg:w-4/5 xl:h-full xl:w-2/3">
          <div className='flex w-full max-w-[1200px] mx-auto align-middle justify-center
          xl:pl-6 2xl:pl-6 mt-4 xl:mt-0 2xl:mt-0'>
            <div className="relative w-full overflow-hidden aspect-[4/3]">
              <div 
                className={`flex ${isDragging ? '' : 'transition-transform duration-300'} ease-in-out  aspect-[4/3]`}
                style={{ 
                    transform: `translateX(calc(-${currentImageIndex * 100}% - ${dragOffset}%))`,
                    cursor: isDragging ? 'grabbing' : 'grab'
                }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                {props.displayImages.map((image, index) => (
                  <img 
                    key={index}
                    src={image} 
                    alt={`Rental ${index + 1}`} 
                    className="object-cover w-full h-full max-h-full aspect-[4/3] flex-shrink-0"
                    draggable="false" // Prevents image dragging on mobile
                  />
                ))}
              </div>
              
              {/* Navigation Buttons */}
              <button 
                onClick={() => handleManualNavigation(currentImageIndex === 0 
                    ? props.displayImages.length - 1 
                    : currentImageIndex - 1)}
                className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 p-2 text-white hover:bg-opacity-75"
              >
                ←
              </button>
              <button 
                onClick={() => handleManualNavigation(currentImageIndex === props.displayImages.length - 1 
                    ? 0 
                    : currentImageIndex + 1)}
                className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 p-2 text-white hover:bg-opacity-75"
              >
                →
              </button>
              
              {/* Dots indicator */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {props.displayImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleManualNavigation(index)}
                    className={`w-2 h-2 rounded-full ${
                      currentImageIndex === index ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="w-full flex flex-col justify-center align pt-4 md:pt-8 xl:pt-0
            xl:hidden 2xl:hidden ">
              <ShowCaseGameText description={props.description}/>
              <LinkedButton text={props.linkedButtonText} link={props.steamLink} />    
              {props.presskitLink && <PresskitButton link={props.presskitLink} />}
          </div>
        </div>
        </div>
      </div>
    )
}